import React, { useState, useEffect } from 'react';
import { userAPI, payoutAPI } from '../../api';

// Defined outside to avoid remount-on-rerender (which kills input focus)
const PasswordField = ({ label, name, value, onChange, placeholder, showPassword, onToggleShow }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <div className="relative">
      <input
        type={showPassword ? 'text' : 'password'}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#E4572E] focus:border-transparent"
      />
      <button
        type="button"
        onClick={onToggleShow}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
      >
        {showPassword ? '🙈' : '👁️'}
      </button>
    </div>
  </div>
);

const UserResetPassword = () => {
  // step 1 = verify current credentials, step 2 = set new passwords
  const [step, setStep] = useState(1);
  const [currentPasswords, setCurrentPasswords] = useState({
    currentPassword: '',
    currentTransactionPassword: '',
  });
  const [newPasswords, setNewPasswords] = useState({
    newPassword: '',
    confirmNewPassword: '',
    newTransactionPassword: '',
    confirmNewTransactionPassword: '',
  });
  const [hasTransactionPassword, setHasTransactionPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingTxn, setCheckingTxn] = useState(true);
  const [message, setMessage] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});

  useEffect(() => {
    const checkTxnPassword = async () => {
      try {
        const res = await payoutAPI.hasTransactionPassword();
        setHasTransactionPassword(res.data.hasTransactionPassword);
      } catch {
        setHasTransactionPassword(false);
      } finally {
        setCheckingTxn(false);
      }
    };
    checkTxnPassword();
  }, []);

  const handleCurrentChange = (e) => {
    setCurrentPasswords((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setMessage(null);
  };

  const handleNewChange = (e) => {
    setNewPasswords((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setMessage(null);
  };

  const toggleShow = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  // Step 1: verify current credentials via API
  const handleVerify = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!currentPasswords.currentPassword) {
      return setMessage({ type: 'error', text: 'Please enter your current login password' });
    }
    if (hasTransactionPassword && !currentPasswords.currentTransactionPassword) {
      return setMessage({ type: 'error', text: 'Please enter your current transaction password' });
    }

    setLoading(true);
    try {
      await userAPI.verifyCredentials({
        currentPassword: currentPasswords.currentPassword,
        ...(hasTransactionPassword && { currentTransactionPassword: currentPasswords.currentTransactionPassword }),
      });
      setMessage(null);
      setStep(2);
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Verification failed. Please try again.';
      setMessage({ type: 'error', text: errMsg });
    } finally {
      setLoading(false);
    }
  };

  // Step 2: submit new passwords
  const handleReset = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (newPasswords.newPassword && newPasswords.newPassword !== newPasswords.confirmNewPassword) {
      return setMessage({ type: 'error', text: 'New login passwords do not match' });
    }
    if (newPasswords.newTransactionPassword && newPasswords.newTransactionPassword !== newPasswords.confirmNewTransactionPassword) {
      return setMessage({ type: 'error', text: 'New transaction passwords do not match' });
    }
    if (!newPasswords.newPassword && !newPasswords.newTransactionPassword) {
      return setMessage({ type: 'error', text: 'Enter at least one new password to update' });
    }

    setLoading(true);
    try {
      const payload = {
        currentPassword: currentPasswords.currentPassword,
        ...(hasTransactionPassword && { currentTransactionPassword: currentPasswords.currentTransactionPassword }),
        ...(newPasswords.newPassword && { newPassword: newPasswords.newPassword }),
        ...(newPasswords.newTransactionPassword && { newTransactionPassword: newPasswords.newTransactionPassword }),
      };
      const res = await userAPI.resetPassword(payload);
      setMessage({ type: 'success', text: res.data.message || 'Password updated successfully' });
      // Reset everything
      setStep(1);
      setCurrentPasswords({ currentPassword: '', currentTransactionPassword: '' });
      setNewPasswords({ newPassword: '', confirmNewPassword: '', newTransactionPassword: '', confirmNewTransactionPassword: '' });
      setShowPasswords({});
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Failed to update password. Please try again.';
      setMessage({ type: 'error', text: errMsg });
    } finally {
      setLoading(false);
    }
  };

  if (checkingTxn) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="w-8 h-8 border-4 border-[#E4572E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#0F2F3D]">Reset Password</h1>
        <div className="flex items-center gap-2 mt-3">
          <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
            step >= 1 ? 'bg-[#E4572E] text-white' : 'bg-gray-200 text-gray-500'
          }`}>1</div>
          <div className={`flex-1 h-1 rounded ${ step >= 2 ? 'bg-[#E4572E]' : 'bg-gray-200' }`} />
          <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
            step >= 2 ? 'bg-[#E4572E] text-white' : 'bg-gray-200 text-gray-500'
          }`}>2</div>
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-xs text-gray-400">Verify Identity</span>
          <span className="text-xs text-gray-400">Set New Password</span>
        </div>
      </div>

      {message && (
        <div className={`mb-5 px-4 py-3 rounded-lg text-sm font-medium ${
          message.type === 'success'
            ? 'bg-green-50 border border-green-200 text-green-700'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? '✅ ' : '❌ '}{message.text}
        </div>
      )}

      {/* ── STEP 1: Verify ── */}
      {step === 1 && (
        <form onSubmit={handleVerify} className="space-y-5">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
            <h2 className="text-base font-semibold text-[#0F2F3D] flex items-center gap-2">
              <span>🔒</span> Verify Your Identity
            </h2>
            <p className="text-xs text-gray-400">Enter your current password(s) to continue.</p>

            <PasswordField
              label="Current Login Password *"
              name="currentPassword"
              value={currentPasswords.currentPassword}
              onChange={handleCurrentChange}
              placeholder="Enter your current login password"
              showPassword={showPasswords.currentPassword}
              onToggleShow={() => toggleShow('currentPassword')}
            />

            {hasTransactionPassword && (
              <PasswordField
                label="Current Transaction Password *"
                name="currentTransactionPassword"
                value={currentPasswords.currentTransactionPassword}
                onChange={handleCurrentChange}
                placeholder="Enter your current transaction password"
                showPassword={showPasswords.currentTransactionPassword}
                onToggleShow={() => toggleShow('currentTransactionPassword')}
              />
            )}

            {!hasTransactionPassword && (
              <p className="text-xs text-gray-400 italic">You have not set a transaction password yet.</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#E4572E] hover:bg-[#c94a26] text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-60"
          >
            {loading ? 'Verifying...' : 'Verify & Continue →'}
          </button>
        </form>
      )}

      {/* ── STEP 2: Set New Passwords ── */}
      {step === 2 && (
        <form onSubmit={handleReset} className="space-y-5">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
            <h2 className="text-base font-semibold text-[#0F2F3D] flex items-center gap-2">
              <span>🔑</span> New Login Password
              <span className="text-xs text-gray-400 font-normal">(optional)</span>
            </h2>

            <PasswordField
              label="New Login Password"
              name="newPassword"
              value={newPasswords.newPassword}
              onChange={handleNewChange}
              placeholder="Min. 6 characters"
              showPassword={showPasswords.newPassword}
              onToggleShow={() => toggleShow('newPassword')}
            />
            <PasswordField
              label="Confirm New Login Password"
              name="confirmNewPassword"
              value={newPasswords.confirmNewPassword}
              onChange={handleNewChange}
              placeholder="Repeat new login password"
              showPassword={showPasswords.confirmNewPassword}
              onToggleShow={() => toggleShow('confirmNewPassword')}
            />
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
            <h2 className="text-base font-semibold text-[#0F2F3D] flex items-center gap-2">
              <span>🔐</span> New Transaction Password
              <span className="text-xs text-gray-400 font-normal">(optional)</span>
            </h2>

            <PasswordField
              label="New Transaction Password"
              name="newTransactionPassword"
              value={newPasswords.newTransactionPassword}
              onChange={handleNewChange}
              placeholder="4–20 characters"
              showPassword={showPasswords.newTransactionPassword}
              onToggleShow={() => toggleShow('newTransactionPassword')}
            />
            <PasswordField
              label="Confirm New Transaction Password"
              name="confirmNewTransactionPassword"
              value={newPasswords.confirmNewTransactionPassword}
              onChange={handleNewChange}
              placeholder="Repeat new transaction password"
              showPassword={showPasswords.confirmNewTransactionPassword}
              onToggleShow={() => toggleShow('confirmNewTransactionPassword')}
            />
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => { setStep(1); setMessage(null); }}
              className="flex-1 border border-gray-300 text-gray-600 font-semibold py-3 rounded-xl hover:bg-gray-50 transition-colors"
            >
              ← Back
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-2 flex-grow bg-[#E4572E] hover:bg-[#c94a26] text-white font-semibold py-3 px-6 rounded-xl transition-colors disabled:opacity-60"
            >
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default UserResetPassword;

